import React, { useEffect, useRef, useState } from 'react';
import api from '@/lib/api';
import { Send, X, Sparkles, MessageCircle, Loader2 } from 'lucide-react';

/**
 * AI chat side-panel (400px right, collapsible).
 * Per-week conversation: conversation_id = `{slug}_{year}_W{week:02d}`.
 * Cross-location isolation enforced server-side.
 */
export default function AIChatPanel({ open, onClose, locationSlug, year, week, cellContext, onCellContextClear }) {
  const conversationId = `${locationSlug}_${year}_W${String(week).padStart(2, '0')}`;
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const scrollRef = useRef(null);

  useEffect(() => {
    if (!open || !locationSlug) return;
    (async () => {
      try {
        const { data } = await api.get(`/ai/chat-history/${conversationId}`);
        setMessages(data.messages || []);
      } catch (e) {
        console.warn('[ai-chat] load failed', e);
      }
    })();
  }, [open, locationSlug, conversationId]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages]);

  async function sendMessage(e) {
    e?.preventDefault();
    const text = input.trim();
    if (!text) return;
    setSending(true);
    setError('');
    // Optimistic add
    setMessages((m) => [...m, { id: `tmp_${Date.now()}`, role: 'user', content: text, created_at: new Date().toISOString() }]);
    setInput('');
    try {
      const { data } = await api.post('/ai/chat', {
        conversation_id: conversationId,
        location_id: locationSlug,
        message: text,
        cell_context: cellContext || null,
      });
      setMessages((m) => [...m, { id: data.message_id, role: 'assistant', content: data.response, usage: data.usage, created_at: new Date().toISOString() }]);
      if (onCellContextClear) onCellContextClear();
    } catch (err) {
      setError(err.response?.data?.detail || 'AI niet bereikbaar');
    } finally {
      setSending(false);
    }
  }

  if (!open) return null;

  return (
    <aside className="fixed right-0 top-0 bottom-0 w-[400px] bg-white border-l border-slate-200 shadow-2xl flex flex-col z-30" data-testid="ai-chat-panel">
      <div className="flex items-center justify-between px-4 py-3 bg-brand-navy text-white">
        <div className="flex items-center gap-2">
          <Sparkles size={16} className="text-brand-amber" />
          <div>
            <p className="text-sm font-semibold">AI Assistent</p>
            <p className="text-[10px] text-white/60">{locationSlug} · Week {week}/{year}</p>
          </div>
        </div>
        <button onClick={onClose} data-testid="ai-chat-close" className="rounded-md p-1 hover:bg-white/10">
          <X size={16} />
        </button>
      </div>

      {cellContext && (
        <div className="bg-amber-50 border-b border-amber-200 px-3 py-2 flex items-center justify-between text-xs">
          <div>
            <span className="text-amber-700 font-semibold">Cel-context: </span>
            <span className="text-amber-900">{cellContext.employee_name} · {cellContext.date}</span>
          </div>
          <button onClick={() => onCellContextClear?.()} className="text-amber-700 hover:text-amber-900">×</button>
        </div>
      )}

      <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-3 space-y-3">
        {messages.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-center px-3">
            <MessageCircle size={32} className="text-slate-300 mb-2" />
            <p className="text-sm text-slate-500">Vraag iets over deze week, een cel, of het rooster.</p>
            <p className="text-xs text-slate-400 mt-1">Bv. “waarom Yves op zaterdag?” of “hebben we genoeg mensen vrijdagmiddag?”</p>
          </div>
        ) : messages.map((m) => (
          <div key={m.id} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
            <div
              data-testid={`ai-msg-${m.role}`}
              className={`max-w-[85%] rounded-lg px-3 py-2 text-sm ${
                m.role === 'user'
                  ? 'bg-brand-navy text-white'
                  : 'bg-slate-100 text-slate-900'
              }`}
            >
              <p className="whitespace-pre-wrap">{m.content}</p>
              {m.usage && (
                <p className="mt-1 text-[10px] opacity-60 tabular-nums">
                  {m.usage.input_tokens + m.usage.output_tokens} tokens · €{((m.usage.cost_usd_cents / 100) * 0.92).toFixed(3)}
                </p>
              )}
            </div>
          </div>
        ))}
        {sending && (
          <div className="flex items-center gap-2 text-xs text-slate-500">
            <Loader2 size={14} className="animate-spin" /> AI denkt na…
          </div>
        )}
      </div>

      {error && <div className="mx-3 mb-2 rounded-md bg-red-50 px-3 py-2 text-xs text-red-700">{error}</div>}

      <form onSubmit={sendMessage} className="border-t border-slate-200 p-3 flex items-center gap-2">
        <input
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder="Stel een vraag…"
          data-testid="ai-chat-input"
          disabled={sending}
          className="flex-1 rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-brand-amber focus:outline-none focus:ring-1 focus:ring-brand-amber/30 disabled:opacity-50"
        />
        <button
          type="submit"
          disabled={sending || !input.trim()}
          data-testid="ai-chat-send"
          className="rounded-md bg-brand-navy p-2 text-white hover:bg-brand-navy-light disabled:opacity-40"
        >
          <Send size={14} />
        </button>
      </form>
    </aside>
  );
}
